import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { WorkFlowService } from '@bcm-work-flows/work-flow.service';
import { BookingType } from '@modules/bcm/@shared/enums/berth-reservation-type';
import { Berth } from '@shared/models/berth';
import { Boat } from '@shared/models/boat';
import { Company } from '@shared/models/company';
import { Person } from '@shared/models/person';
import { BookingDialogComponent } from '@sharedComponents/dialogs/booking-dialog/booking-dialog.component';

import { BookingV2DialogData } from '../booking-v2-dialog.component';
import { BookingV2BoatDimensions } from '../dtos/booking-v2.dtos';
import { BookingV2Type } from '../services/booking-v2.facade';
import { toBerthOption, toBoatOption, toCompanyOption, toManualBoatOption, toPersonOption } from '../utils/booking-v2-option.mappers';
import { BetaFeaturesService } from './beta-features.service';
import { BookingV2DialogService } from './booking-v2-dialog.service';

// Boat context an entry point can hand over — a picked boat, or (map search
// without one) plain dimensions. Boat wins when both are somehow present.
export interface BookingEntryBoatPrefill {
    boat?: Boat | null;
    boatProperties?: BookingV2BoatDimensions | null;
}

/**
 * Single routing layer for *starting a new booking* from any entry point.
 *
 * Checks the `betaFeatures.bookingDialogV2` flag and opens either the new
 * booking-v2 wizard (with the context prefilled) or the legacy BookingDialog —
 * so the entry-point components stay free of both the flag and the two dialogs'
 * differing open mechanisms. Editing an existing booking goes through
 * `editBooking()`, which routes the same way (v2 wizard vs. legacy dialog).
 *
 * Every method returns a unified observable that emits once when the dialog
 * closes: a truthy value means a booking was created, falsy means cancelled.
 * That matches the existing `subscribe(value => { if (value) reload })` call
 * sites, whether the underlying dialog is legacy (MatDialog `afterClosed()`)
 * or v2 (PrimeNG `DynamicDialogRef.onClose`).
 *
 * This flag-gated shape is the template for upcoming beta features: add a flag
 * to BetaFeaturesService and one intent method that branches v2 vs legacy.
 */
@Injectable({ providedIn: 'root' })
export class BookingEntryService {

    private readonly beta = inject(BetaFeaturesService);
    private readonly v2Dialog = inject(BookingV2DialogService);
    private readonly workFlow = inject(WorkFlowService);
    private readonly matDialog = inject(MatDialog);

    // Boat detail → "assign berth". The boat's owner rides along on the boat
    // option and defaults the person step in v2.
    startBookingForBoat(boat: Boat): Observable<unknown> {
        if (this.beta.bookingDialogV2()) {
            return this.openV2({ bookingType: 'booking', boat: toBoatOption(boat) });
        }
        // Mirrors boat-core's previous legacy open exactly.
        return this.matDialog.open(BookingDialogComponent, {
            data: { preselect: { boat } },
            panelClass: ['default-dialog', 'xxl-dialog'],
        }).afterClosed();
    }

    // Berth detail / map context menu → "new booking" / "new reservation".
    startBookingForBerths(
        berths: Berth[],
        type: BookingType,
        dateRange?: { from?: Date | null; to?: Date | null },
        prefill?: BookingEntryBoatPrefill,
    ): Observable<unknown> {
        if (this.beta.bookingDialogV2()) {
            return this.openV2({
                bookingType: this.toV2Type(type),
                berths: berths.map(toBerthOption),
                dateRange: dateRange
                    ? { from: dateRange.from ?? null, to: dateRange.to ?? null, openEnd: !dateRange.to }
                    : undefined,
                boat: this.toBoatPrefill(prefill),
            });
        }
        return this.workFlow.newBerthReservation(
            berths,
            type,
            dateRange?.from ?? undefined,
            dateRange?.to ?? undefined,
        );
    }

    // Map editor sidebar: may carry multiple selected berths. The v2 wizard
    // collects the date range in its own step; the legacy path keeps
    // WorkFlowService's multi-berth date-range prompt (addBerthAssignment).
    startBerthAssignment(
        berths: Berth[],
        type: BookingType,
        dateRange?: { start: Date | null; end: Date | null },
        prefill?: BookingEntryBoatPrefill,
    ): Observable<unknown> {
        if (this.beta.bookingDialogV2()) {
            return this.openV2({
                bookingType: this.toV2Type(type),
                berths: berths.map(toBerthOption),
                dateRange: dateRange?.start
                    ? { from: dateRange.start, to: dateRange.end, openEnd: !dateRange.end }
                    : undefined,
                boat: this.toBoatPrefill(prefill),
            });
        }
        // addBerthAssignment is async (Promise<Observable | undefined>) and may
        // resolve undefined when its date prompt is cancelled — flatten both.
        return from(this.workFlow.addBerthAssignment(berths, type, dateRange)).pipe(
            switchMap(inner => inner ?? of(undefined)),
        );
    }

    // Person detail → new "create booking" button. v2-only: the button is
    // hidden when the flag is off (the legacy dialog has no person-booking
    // entry), so only the v2 branch can run. The booking person is fixed.
    startBookingForPerson(person: Person | Company, opts: { kind: 'person' | 'company' }): Observable<unknown> {
        const personOption = opts.kind === 'company'
            ? toCompanyOption(person as Company)
            : toPersonOption(person as Person);
        return this.openV2({
            bookingType: 'booking',
            person: personOption,
            // Person chosen explicitly — do not let a later boat pick override it.
            skipOwnerDefault: true,
        });
    }

    // Dashboard widget → context-less "new booking" / "new reservation".
    startBlankBooking(type: BookingType): Observable<unknown> {
        if (this.beta.bookingDialogV2()) {
            return this.openV2({ bookingType: this.toV2Type(type) });
        }
        return this.workFlow.newBerthReservation(undefined, type);
    }

    // Edit entry point: v2 wizard loads + reconciles by id; legacy dialog uses the
    // same `{ booking: { id } }` shape the call sites already passed.
    editBooking(bookingId: number): Observable<unknown> {
        if (this.beta.bookingDialogV2()) {
            return this.openV2({ bookingId });
        }
        return this.matDialog.open(BookingDialogComponent, {
            data: { booking: { id: bookingId } },
            panelClass: ['default-dialog', 'xxl-dialog'],
        }).afterClosed();
    }

    private openV2(data: BookingV2DialogData): Observable<unknown> {
        // The dialog handles its own post-save Beleg redirect; a truthy close
        // result means "created/updated → reload" for every call site.
        return this.v2Dialog.open(data).onClose.pipe(map(result => result ?? undefined));
    }

    // Legacy resolves the map's boat itself (WorkFlowService.newBerthReservation
    // reads the last filter), so this only feeds the v2 wizard.
    private toBoatPrefill(prefill?: BookingEntryBoatPrefill): BookingV2DialogData['boat'] {
        if (prefill?.boat) {
            return toBoatOption(prefill.boat);
        }
        return prefill?.boatProperties ? toManualBoatOption(prefill.boatProperties) : undefined;
    }

    private toV2Type(type: BookingType): BookingV2Type {
        return type === BookingType.Booking ? 'booking' : 'reservation';
    }
}
